import {
  getFirestore,
  connectFirestoreEmulator,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { app } from '../firebase';
import { normalizeMarks } from './markNormalize';

const db = getFirestore(app);
const functions = getFunctions(app);
// Firebase Storage — CGC (Club Generated Content) document uploads (ra-8ggy). Same bucket as the
// read-only /content bundles (racemarksapp.firebasestorage.app).
const storage = getStorage(app);

// Connect to emulators only when explicitly opted in
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  } catch {
    // Already connected
  }
}

function stripUndefined(obj) {
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)]),
    );
  }
  return obj;
}

export async function getClubMarks(clubId) {
  const snap = await getDocs(collection(db, 'clubs', clubId, 'marks'));
  // Normalize to { id, latitude:number, longitude:number } so any stored shape
  // (lat/lng, string coords, nested, mark_id) renders + can't be zeroed on save.
  // A stored mark code (id / mark_id) wins over the Firestore doc id so course
  // markIds — which reference the mark code — still resolve (ra-or1y, codex).
  return normalizeMarks(snap.docs.map((d) => {
    const data = d.data();
    return { ...data, id: data.id ?? data.mark_id ?? data.markId ?? d.id };
  }));
}

export async function republishBundle(clubId, sourceBundleId) {
  const fn = httpsCallable(functions, 'republishCourseBundle');
  const result = await fn({ clubId, sourceBundleId });
  return result.data;
}

export async function getPublishedBundles(clubId) {
  const q = query(
    collection(db, 'clubs', clubId, 'courseBundles'),
    where('status', '==', 'published'),
  );
  const snap = await getDocs(q);
  const published = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  published.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
  return published;
}

export async function getEventPublishedBundles(clubId, eventId) {
  const snap = await getDocs(
    collection(db, 'clubs', clubId, 'events', eventId, 'bundle'),
  );
  const published = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  published.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
  return published;
}

export async function getAllBundles(clubId) {
  const snap = await getDocs(collection(db, 'clubs', clubId, 'courseBundles'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Get all events for a club (seasons like "2025-summer"). */
export async function getClubEvents(clubId) {
  const snap = await getDocs(collection(db, 'clubs', clubId, 'events'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Get the published bundle for an event at a specific version. */
export async function getEventBundle(clubId, eventId, version = 1) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId, 'bundle', `v${version}`);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getBundleById(clubId, bundleId) {
  const ref = doc(db, 'clubs', clubId, 'courseBundles', bundleId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// WIP (work-in-progress) functions — new storage model (Phase 2)
export async function saveWip(clubId, eventId, data) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId, 'wip', 'vNext');
  await setDoc(ref, {
    ...stripUndefined(data),
    updated_at: serverTimestamp(),
  }, { merge: true });
}

export async function loadWip(clubId, eventId) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId, 'wip', 'vNext');
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: 'vNext', ...snap.data() };
}

export async function deleteWip(clubId, eventId) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId, 'wip', 'vNext');
  await deleteDoc(ref);
}

// Cheap done-state signals for the club setup checklist (ra-gbrd.12). Each
// reads a single doc / small collection so the dashboard can derive a ✓ per
// step without deep validation.

/** Draft (wip) course cards for a season, or [] when there's no draft yet. */
export async function getEventDraftCards(clubId, eventId) {
  const wip = await loadWip(clubId, eventId);
  return wip?.course_cards || [];
}

/** Racetime variants for a season (each may carry a `starts` array). */
export async function getEventVariants(clubId, eventId) {
  const snap = await getDocs(
    collection(db, 'clubs', clubId, 'events', eventId, 'racetime_variants'),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function publishWip(clubId, eventId) {
  const fn = httpsCallable(functions, 'publishCourseBundle');
  const result = await fn({ clubId, eventId });
  return result.data;
}

export async function activateBundle(clubId, eventId, version) {
  const fn = httpsCallable(functions, 'activateCourseBundle');
  const result = await fn({ clubId, eventId, version });
  return result.data;
}

export async function getEventDoc(clubId, eventId) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// Point the event's APP-PREVIEW pointer at a published bundle version WITHOUT
// activating it (ra-yuuv). Club/global admins can then preview that version in
// the app (Club Content → load preview bundle) before making it live. Mirrors
// activateCourseBundle's content_url format, but writes the preview_* fields the
// app reads (event_model.dart: preview_content_version / preview_content_url).
// Direct client write — firestore.rules allows isAdmin || hasPortalWriteAccess
// on the event doc, the same access activate requires.
export async function setEventPreviewBundle(clubId, eventId, version) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId);
  await updateDoc(ref, {
    preview_content_version: version,
    preview_content_url: `firestore://clubs/${clubId}/events/${eventId}/bundle/v${version}`,
  });
}

// Remove the app-preview pointer (admins fall back to the active version).
export async function clearEventPreviewBundle(clubId, eventId) {
  const ref = doc(db, 'clubs', clubId, 'events', eventId);
  await updateDoc(ref, {
    preview_content_version: null,
    preview_content_url: null,
  });
}

// ── Course/Track Viewer admin framing (ra-97jo) ─────────────────────
// Both back the committee Course/Track Viewer's session picker so the client
// never reads the events/ tree directly (admin-only callables do the walk).

// List recorded sessions across a date range (YYYY-MM-DD). Returns
// { fetchedAt, fromDate, toDate, dayCount, sessions } — sessions are
// sessionGrouping-shaped (vesselId, fleetClass, devices[], …).
export async function listHistoricalSessions(fromDate, toDate) {
  const fn = httpsCallable(functions, 'listHistoricalSessions');
  const result = await fn({ fromDate, toDate });
  return result.data;
}

// Fetch a recorded session's boat track, built server-side from its
// position_recorded / instrument_recorded events. Returns
// { sessionPath, meta, track, inst, summary } — `track`/`inst` are
// data.json-shaped for trackOverlay.parseTrackData.
export async function getSessionTrack(sessionPath) {
  const fn = httpsCallable(functions, 'getSessionTrack');
  const result = await fn({ sessionPath });
  return result.data;
}

export { db, storage, Timestamp };
